import { useId } from "react";
import { Check } from "lucide-react";
import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { SectionHeader } from "@/shared/components/ui/SectionHeader";
import type { SubscriptionOffer, SubscriptionSku } from "../types/subscriptionTypes";
import { formatOfferPrice } from "../utils/subscriptionFormat";

function offerFeatures(offer: SubscriptionOffer) {
  return [
    "Unlimited email confirmations and reminders",
    ...(offer.features.includes("sms") ? [`${offer.limits.sms} customer text messages per month`] : []),
    ...(offer.features.includes("ai") ? ["AI-drafted replies and scheduling assistant"] : []),
  ];
}

function PlanOffer({ offer, busy, onUpgrade }: { offer: SubscriptionOffer; busy: boolean; onUpgrade: () => void }) {
  const headingId = useId();
  return (
    <Card tone={offer.features.includes("ai") ? "ai" : "default"} role="region" aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="space-y-1">
        <SectionHeader title={offer.name} headingId={headingId} />
        <p className="text-sm font-bold">{formatOfferPrice(offer)}</p>
      </div>
      <ul className="space-y-2 text-sm">
        {offerFeatures(offer).map((feature) => (
          <li key={feature} className="flex items-start gap-1.5">
            <Check className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {feature}
          </li>
        ))}
      </ul>
      <Button className="mt-auto" disabled={busy} onClick={onUpgrade}>
        {busy ? "Opening Stripe..." : `Upgrade to ${offer.name}`}
      </Button>
    </Card>
  );
}

interface PlanOffersProps {
  offers: SubscriptionOffer[];
  busy: boolean;
  onUpgrade: (sku: SubscriptionSku) => void;
}

export function PlanOffers({ offers, busy, onUpgrade }: PlanOffersProps) {
  if (offers.length === 0) {
    return <EmptyState title="Plans unavailable" message="Upgrades are unavailable right now. Refresh your plan to try again." />;
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {offers.map((offer) => (
        <PlanOffer key={offer.sku} offer={offer} busy={busy} onUpgrade={() => onUpgrade(offer.sku)} />
      ))}
    </div>
  );
}
